import React from 'react';
import type { Components } from 'blip-ds/loader';

type BannerVariant = 'warning' | 'system' | 'info' | 'error' | 'success';
type BannerContext = 'inside' | 'outside';
type BannerLinkTarget = 'blank' | 'self' | 'parent' | 'top' | 'framename' | '_blank' | '_self' | '_parent' | '_top';
type BdsBannerLinkTarget = 'blank' | 'self' | 'parent' | 'top' | 'framename';

export interface WarningBannerProps {
  message: string;
  link?: string;
  linkText?: string;
  className?: string;
  messageClassName?: string;
  variant?: BannerVariant;
  context?: BannerContext;
  close?: boolean;
  linkTarget?: BannerLinkTarget;
  onClose?: () => void;
}

export const WarningBanner: React.FC<WarningBannerProps> = ({
  message,
  link,
  linkText,
  className,
  messageClassName,
  variant = 'warning',
  context = 'outside',
  close = true,
  linkTarget = '_blank',
  onClose,
}) => {
  const bannerRef = React.useRef<(Components.BdsBanner & HTMLBdsBannerElement) | null>(null);

  React.useEffect(() => {
    if (!onClose || !bannerRef.current) {
      return;
    }

    const bannerElement = bannerRef.current;
    const handleClose = () => onClose();

    bannerElement.addEventListener('bdsBannerClose', handleClose);

    return () => {
      bannerElement.removeEventListener('bdsBannerClose', handleClose);
    };
  }, [onClose]);

  const normalizedLinkTarget: BdsBannerLinkTarget = linkTarget.startsWith('_')
    ? (linkTarget.slice(1) as BdsBannerLinkTarget)
    : (linkTarget as BdsBannerLinkTarget);

  return (
    <div className={className}>
      <bds-banner ref={bannerRef} variant={variant} context={context} buttonClose={close ? 'true' : 'false'}>
        <span className={messageClassName}>{message}</span>
        {link && linkText && (
          <bds-banner-link link={link} target={normalizedLinkTarget}>
            {linkText}
          </bds-banner-link>
        )}
      </bds-banner>
    </div>
  );
};
